<template>
  <div class='three-visual'>
    <Loader v-if='loading' class='loading-indicator' />
    <div class='errors' v-if='dimensionErrors.length > 0'>
      <Alert>
        {{ $t('visual.placeholderOverflow') }} <strong>{{ dimensionErrors.join(', ') }}</strong>.
      </Alert>
    </div>
    <div ref='container'></div>
    <div class='overlay' :class='{hidden: blister}'>
      <span>{{ $t('visual.noVisualYet') }}</span>
    </div>
    <div class='controls'>
      <blister-info v-if='blister' :blister='blister' />
      <div class='control' @click='autoRotate = !autoRotate' :class='{active: autoRotate}'><img
        src='../assets/autorotate.svg' alt=''></div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import BlisterMaterial from '../3d/BlisterMaterial'
import {
  createPlaceholder,
  placeholderMaterial,
  invalidPlaceholderMaterial,
  placeholderMinHeight
} from '../3d/Placeholder'
import Renderer from '../3d/Renderer'
import Scene from '../3d/Scene'
import OrbitControls from '../3d/OrbitControls'
import BlisterInfo from './BlisterInfo'
import Alert from './Alert'
import Loader from './Loader'

export default {
  name: 'ThreeVisual',
  components: { BlisterInfo, Alert, Loader },
  props: {
    blister: {
      type: Object
    },
    placeholderDimensions: {
      type: Object
    }
  },
  data () {
    return {
      loading: false,
      controls: null,
      width: 500,
      height: 500,
      blisterNode: null,
      placeholderNode: null,
      autoRotate: true,
      dimensionErrors: []
    }
  },
  mounted () {
    let scene = Scene
    this.scene = scene

    this.renderer = Renderer
    this.renderer.setSize(this.width, this.height)

    this.camera = new THREE.PerspectiveCamera(45, this.width / this.height, 0.1, 1000)
    let camera = this.camera

    var webglContainer = this.$refs.container
    webglContainer.appendChild(this.renderer.domElement)

    this.registerResizeObserver()

    // position and point the camera to the center of the scene
    camera.position.x = -70
    camera.position.y = 40
    camera.position.z = 70
    camera.lookAt(scene.position)

    this.controls = OrbitControls(this.camera, this.renderer)

    this.loadModel('BB1_10mm.glb')

    this.render() // dont call render function another time
  },
  methods: {
    registerResizeObserver () {
      const resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          this.resize(entry.contentRect.width, entry.contentRect.height)
        }
      })
      resizeObserver.observe(this.$el)
    },
    resize (width, height) {
      this.width = width
      this.height = height
      this.camera.aspect = this.width / this.height
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(this.width, this.height)
    },
    render () {
      requestAnimationFrame(this.render)
      // required if controls.enableDamping or controls.autoRotate are set to true
      this.controls.update()
      this.renderer.render(this.scene, this.camera)
    },
    centerModel (node) {
      let mesh = null
      let walk = function(n) {
        for (let child of n.children) {
          if (child.type === 'Mesh') {
            mesh = child
            break
          }
          walk(child)
        }
      }
      walk(node)
      if (mesh) mesh.geometry.center()
    },
    rotateModel (node) {
      node.rotation.x = Math.PI / -2
    },
    removeBlackCap (node) {
      if (node.children[0].children.length > 1) {
        node.children[0].children[1].removeFromParent()
      }
    },
    applyBlisterMaterial (node) {
      node = node.children[0].children[0] || node.children[0] || node
      node.material = BlisterMaterial
    },
    preprocessModel (node) {
      /*
       * Models that were exported by Medipack are not
       * manually optimized to fit the requirements of this tool.
       * Therefore we'll have to make sure that all models are properly
       * centered, rotated and have unwanted parts removed (black blister cap).
       */
      // this.centerModel(node)
      this.rotateModel(node)
      this.removeBlackCap(node)
      this.applyBlisterMaterial(node)
    },
    loadModel (model) {
      this.loading = true
      const loader = new GLTFLoader()
      const baseDir = process.env.NODE_ENV === 'production'
        ? '/integrations/blister-finder'
        : '.'
      const url = `${baseDir}/models/${model}`

      loader.load(url, (gltf) => {
        const root = gltf.scene
        let blister = root.children[0]
        blister.castShadow = true
        blister.position.set(0, 0, 0)
        let modelScaleFactor = 1000 // needed to make 1mm equal to 1 world unit
        blister.scale.set(modelScaleFactor, modelScaleFactor, modelScaleFactor)

        if (this.blisterNode) {
          this.scene.remove(this.blisterNode)
        }

        this.blisterNode = root
        this.scene.add(this.blisterNode)
        this.preprocessModel(this.blisterNode)

        // Display bounding boxes
        // this.scene.add(new THREE.BoxHelper(this.blisterNode))

        // Line https://threejs.org/docs/#api/en/objects/Line
        // const material = new THREE.LineBasicMaterial({
        //   color: 0x8000ff
        // })
        // const points = []
        // points.push(new THREE.Vector3(0, 0, -100))
        // points.push(new THREE.Vector3(0, 0, 100))
        // const geometry = new THREE.BufferGeometry().setFromPoints(points)
        // const line = new THREE.Line(geometry, material)
        // this.scene.add(line)

        this.loading = false
      }, null, error => {
        alert(`Unable to load model "${url}".`)
        console.error(`Unable to load model "${url}": ${error}`)
        this.loading = false
        this.blister = null
      })
    },
    loadModelByHeight (modelName, height) {
      let fileName = `${modelName.replace(/\+L0|\+L/, '+', '+')}_${height}mm.glb`
      this.loadModel(fileName)
    },
    updatePlaceholder (width, depth, height) {
      if (this.placeholderNode) {
        this.scene.remove(this.placeholderNode)
        this.placeholderNode.add(new THREE.BoxHelper(this.placeholderNode))
      }
      this.placeholderNode = createPlaceholder(width, depth, height)
      this.scene.add(this.placeholderNode)
      // align placeholder on top plane of blister
      let blisterBox = new THREE.Box3()
      blisterBox.setFromObject(this.blisterNode)
      let yOffset = -1 * Math.max(height / 2, placeholderMinHeight / 2)
      this.placeholderNode.translateY(yOffset)
    },
    validatePlaceholderDimensions (dimensions) {
      this.dimensionErrors = []
      const widthValid = dimensions.width <= this.blister.width
      const heightValid = dimensions.height <= this.blister.height
      const depthValid = dimensions.depth <= this.blister.length
      const isValid = widthValid && heightValid && depthValid
      if (!isValid) {
        if (!widthValid) this.dimensionErrors.push(this.$t('filter.width'))
        if (!heightValid) this.dimensionErrors.push(this.$t('filter.height'))
        if (!depthValid) this.dimensionErrors.push(this.$t('filter.length'))
      }
      return isValid
    }
  },
  watch: {
    blister (newBlister) {
      if (newBlister) this.loadModelByHeight(newBlister.name, newBlister.height)
    },
    placeholderDimensions (newDimensions) {
      if (!newDimensions.width) newDimensions.width = 0
      if (!newDimensions.depth) newDimensions.depth = 0
      if (!newDimensions.height) newDimensions.height = 0
      if (this.blister) {
        this.updatePlaceholder(newDimensions.depth, newDimensions.width, newDimensions.height)
        if (this.validatePlaceholderDimensions(newDimensions)) {
          this.placeholderNode.material = placeholderMaterial
        } else {
          this.placeholderNode.material = invalidPlaceholderMaterial
        }
      }
    },
    autoRotate () {
      this.controls.autoRotate = this.autoRotate
    }
  }

}
</script>

<style lang='scss' scoped>
.three-visual {
  width: 100%;
  height: 300px;
  position: relative;
  max-width: 100%;

  @media (min-width: 900px) {
    height: 500px;
  }

  .overlay {
    background-color: rgba(#000, 0.2);
    backdrop-filter: blur(20px);
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    font-weight: bold;
    font-size: 20px;
    transition: opacity ease 300ms;
    text-shadow: 2px 2px 1px rgb(0 0 0 / 10%);

    &.hidden {
      pointer-events: none;
      opacity: 0;
    }

    span {
      max-width: 300px;
      text-align: center;
    }
  }

  .errors {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }

  .controls {
    display: flex;
    align-items: center;
    gap: 15px;
    position: absolute;
    bottom: 0;
    right: 0;
    padding: 15px;


    .control {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid rgba(#fff, 0.4);
      cursor: pointer;

      &.active {
        background-color: rgba(#fff, 0.2);
      }

      img {
        max-width: 60%;
      }
    }
  }

  .loading-indicator {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
  }
}
</style>